import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, STATUS_META } from "@/lib/entries";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import EntryDialog from "@/components/EntryDialog";
import StatusBadge from "@/components/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PhoneCall, Star, FlaskConical, ClipboardList, Plus, ArrowUpRight, CalendarClock, AlertTriangle, MessageCircle } from "lucide-react";

function todayNice() {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
}

const CARDS = [
  { key: "followups_due",      label: "Follow-ups due",     icon: PhoneCall,     tone: "sky",     testid: "stat-followups-due",     to: "/follow-ups" },
  { key: "open_conversations", label: "In Conversation",    icon: MessageCircle, tone: "emerald", testid: "stat-open-conversations", to: "/follow-ups" },
  { key: "reviews_due",        label: "Reviews due",        icon: Star,          tone: "emerald", testid: "stat-reviews-due",       to: "/reviews" },
  { key: "labs_pending",       label: "Lab work",           icon: FlaskConical,  tone: "violet",  testid: "stat-labs-pending",      to: "/lab-work" },
  { key: "todays_log",         label: "Today\u2019s log",   icon: ClipboardList, tone: "slate",   testid: "stat-todays-log",        to: "/daily-log" },
];

const TONE_ICON = {
  sky:     "bg-sky-50 text-sky-700",
  emerald: "bg-emerald-50 text-emerald-700",
  violet:  "bg-violet-50 text-violet-700",
  slate:   "bg-slate-100 text-slate-700",
  orange:  "bg-orange-50 text-orange-700",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [labOverdueDays, setLabOverdueDays] = useState(7);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        api.get("/dashboard"),
        api.get("/settings"),
      ]);
      setData(d.data);
      setLabOverdueDays(s.data?.lab_overdue_days ?? 7);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const stats = data?.stats || {};

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{todayNice()}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Today&apos;s pending tasks</h1>
        </div>
        <Button className="bg-sky-500 hover:bg-sky-600 text-white h-11 px-5"
                onClick={() => setOpen(true)} data-testid="new-entry-button">
          <Plus size={18} className="mr-1.5" /> New log entry
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {CARDS.map(({ key, label, icon: Icon, tone, testid, to }) => (
          <Link to={to} key={key} data-testid={testid} className="block">
            <Card className="p-5 border-slate-200 hover-lift">
              <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${TONE_ICON[tone]}`}>
                <Icon size={18} />
              </div>
              <div className="mt-4 text-3xl font-display font-bold text-slate-900">
                {data ? (stats[key] ?? 0) : "—"}
              </div>
              <div className="text-xs text-slate-500 mt-1">{label}</div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Follow-ups due */}
      <SectionCard title="Follow-ups due today" subtitle="Ongoing treatments and cancellations to reach out to."
                   linkTo="/follow-ups" testid="section-followups"
                   icon={PhoneCall} rows={data?.followups_today || []}
                   emptyText="No follow-up calls scheduled for today."
                   columns={[
                     { head: "Patient", cell: (e) => e.patient_name, className: "font-medium text-slate-900" },
                     { head: "Status",  cell: (e) => <StatusBadge status={e.status} /> },
                     { head: "Due",     cell: (e) => <span className={((e.scheduled_date || e.followup_date) < data.today) ? "text-amber-700 font-medium" : "text-slate-700"}>{fmtDate(e.scheduled_date || e.followup_date)}</span> },
                     { head: "Notes",   cell: (e) => <span className="text-slate-600 line-clamp-1">{e.notes || "—"}</span>, className: "max-w-xs truncate" },
                   ]} />

      {/* Reviews due */}
      <SectionCard title="Review requests to send" subtitle="Treatment completed patients waiting for a review nudge."
                   linkTo="/reviews" testid="section-reviews"
                   icon={Star} rows={data?.reviews_due || []}
                   emptyText="No review reminders due today. Nice."
                   columns={[
                     { head: "Patient",   cell: (e) => e.patient_name, className: "font-medium text-slate-900" },
                     { head: "Reminder",  cell: (e) => <span className="text-slate-700">#{e.review_stage} of 3</span> },
                     { head: "Since",     cell: (e) => <span className="text-slate-500">{fmtDate(e.entry_date)}</span> },
                     { head: "Notes",     cell: (e) => <span className="text-slate-600 line-clamp-1">{e.notes || "—"}</span>, className: "max-w-xs truncate" },
                   ]} />

      {/* Lab work pending */}
      <SectionCard title="Lab work outstanding"
                   subtitle={stats.lab_turnaround_avg != null
                     ? `Waiting for lab to deliver · avg turnaround ${stats.lab_turnaround_avg}d (${stats.lab_turnaround_count} completed)`
                     : "Waiting for lab to deliver."}
                   linkTo="/lab-work" testid="section-labs"
                   icon={FlaskConical} rows={data?.labs_pending || []}
                   emptyText="No pending lab work."
                   columns={[
                     { head: "Patient", cell: (e) => e.patient_name, className: "font-medium text-slate-900" },
                     { head: "Ordered", cell: (e) => <span className="text-slate-500">{fmtDate(e.entry_date)}</span> },
                     { head: "Days open", cell: (e) => {
                         const days = Math.max(0, Math.floor((Date.now() - new Date(e.entry_date + "T00:00:00").getTime()) / 86400000));
                         const overdue = days > (labOverdueDays ?? 7);
                         return <span className={overdue ? "text-amber-700 font-semibold inline-flex items-center gap-1" : "text-slate-700"}>{days}d{overdue && <AlertTriangle size={12} />}</span>;
                       } },
                     { head: "Notes",   cell: (e) => <span className="text-slate-600 line-clamp-1">{e.notes || "—"}</span>, className: "max-w-xs truncate" },
                   ]} />

      <EntryDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}

function SectionCard({ title, subtitle, linkTo, testid, icon: Icon, rows, emptyText, columns }) {
  return (
    <Card className="mt-6 border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
            <Icon size={16} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          </div>
        </div>
        <Link to={linkTo} className="text-sm text-sky-600 hover:text-sky-700 inline-flex items-center gap-1">
          Open sheet <ArrowUpRight size={14} />
        </Link>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">{emptyText}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c, i) => <TableHead key={i}>{c.head}</TableHead>)}
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.id} data-testid={`${testid}-row-${e.id}`}>
                {columns.map((c, i) => (
                  <TableCell key={i} className={c.className}>{c.cell(e)}</TableCell>
                ))}
                <TableCell className="text-right">
                  <Link to={`/entries/${e.id}`}>
                    <Button size="sm" variant="outline" data-testid={`open-entry-${e.id}`}>Open</Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
